import _defineProperty from "/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { toDocumentField } from "../models/fields";
import { isAcronymic, uncapitalize } from "../util";
import { BusinessCardSchema } from "./modelSchemas/businessCard";
import { HealthInsuranceCardUsSchema } from "./modelSchemas/healthInsuranceCard";
import { IdentityDocumentSchema } from "./modelSchemas/idDocument";
import { InvoiceSchema } from "./modelSchemas/invoice";
import { ReceiptSchema } from "./modelSchemas/receipt";
import { VaccinationCardSchema } from "./modelSchemas/vaccinationCard";
import { TaxUsW2Schema } from "./modelSchemas/w2"; // This symbol is used to index the transformation function. We might replace this with a normal string property key
// later, but for now this symbol allows us to hide it.

var fromDocument = Symbol("fromDocument");
/**
 * Checks a field value against a schema and converts it into a strong idiomatic DocumentField,
 * @internal
 * @param fieldName - the name of the field (used in diagnostics)
 * @param schema - the field's schema
 * @param field - the raw DocumentField value
 * @returns
 */

function extractField(fieldName, schema, field) {
  if (schema.type !== field.kind) {
    throw new Error("Schema violation: ".concat(fieldName, " had type \"").concat(field.kind, "\", but expected \"").concat(schema.type, "\""));
  } // Objects need to be handled specially, so that we can camelCase the field names.


  if (field.kind === "object") {
    var result = {};

    for (var _i = 0, _Object$entries = Object.entries(schema.properties); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          subFieldName = _Object$entries$_i[0],
          subFieldSchema = _Object$entries$_i[1];

      if (field.properties[subFieldName] !== undefined && field.properties[subFieldName] !== null) {
        result[isAcronymic(subFieldName) ? subFieldName : uncapitalize(subFieldName)] = extractField(fieldName + "." + subFieldName, subFieldSchema, field.properties[subFieldName]);
      }
    }

    return Object.assign(Object.assign({}, field), {
      properties: result
    });
  } else if (field.kind === "array") {
    return Object.assign(Object.assign({}, field), {
      values: field.values.map(function (val, idx) {
        return extractField(fieldName + "[" + idx + "]", schema.items, val);
      })
    });
  } else return field;
}
/**
 * Create a model that can extract documents using the given schema.
 * @internal
 * @param schema - model schema contents (see modelSchemas directory)
 * @returns - a DocumentModel that encodes the schema
 */


function createModelFromSchema(schema) {
  return _defineProperty({
    modelId: schema.modelId
  }, fromDocument, function (documentValue) {
    var document = documentValue;
    var result = {};
    var model = schema.docTypes[document.docType];

    if (model === undefined) {
      throw new Error("Unexpected document type \"".concat(document.docType, "\" in result using model \"").concat(schema.modelId, "\""));
    }

    for (var _i2 = 0, _Object$entries2 = Object.entries(model.fieldSchema); _i2 < _Object$entries2.length; _i2++) {
      var _Object$entries2$_i = _slicedToArray(_Object$entries2[_i2], 2),
          fieldName = _Object$entries2$_i[0],
          fieldSchema = _Object$entries2$_i[1];

      if (document.fields && document.fields[fieldName] !== undefined && document.fields[fieldName] !== null) {
        result[isAcronymic(fieldName) ? fieldName : uncapitalize(fieldName)] = extractField(fieldName, fieldSchema, toDocumentField(document.fields[fieldName]));
      }
    }

    return {
      docType: document.docType,
      fields: result
    };
  });
}
/**
 * Extract the mapper from a model object.
 * @internal
 * @param model - a DocumentModel object
 * @returns - a function that transforms base AnalyzeResult Document objects into the DocumentModel's Document objects
 */


export function getMapper(model) {
  return model[fromDocument];
}
/**
 * **Deprecation Warning**: This object, along with all of its fields, is deprecated and will be replaced prior to a
 * stable release of `@azure/ai-form-recognizer` 4.0.0. Please see
 * [the deprecation notice](https://github.com/Azure/azure-sdk-for-js/blob/main/sdk/formrecognizer/ai-form-recognizer/README.md#prebuiltmodels-deprecation)
 * in the README for more information.
 *
 * Prebuilt models with well-known document types and corresponding field schemas.
 *
 * These prebuilt `DocumentModel` objects can be used with the `beginAnalyzeDocuments` method to extract documents with
 * a stronger TypeScript result type and property names that have been converted to use a JavaScript naming convention.
 *
 * ### Example:
 *
 * ```javascript
 * import { AzureKeyCredential, DocumentAnalysisClient, PrebuiltModels } from "@azure/ai-form-recognizer";
 *
 * const credential = new AzureKeyCredential("<API key>">);
 * const client = new DocumentAnalysisClient("<endpoint>", credential);
 *
 * const poller = await client.beginAnalyzeDocuments(PrebuiltModels.Receipt, "<receipt file URL>");
 *
 * // Extract the first document from the response, which should be a receipt
 * const { documents: [receipt] } = await poller.pollUntilDone();
 *
 * // If we had used the model ID "prebuilt-receipt" instead of the `PrebuiltModels.Receipt` data structure, then the
 * // fields of the receipt would be in PascalCase (rather than camelCase), and the TypeScript type of the fields would
 * // not be as strict. This reduces the burden of casting fields to the appropriate types.
 *
 * console.log("The type of this receipt is:", receipt.fields.receiptType?.value)
 * ```
 *
 * @deprecated will be removed and replaced with auto-generated code in the next stable version
 */

export var PrebuiltModels = {
  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  BusinessCard: createModelFromSchema(BusinessCardSchema),

  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  IdentityDocument: createModelFromSchema(IdentityDocumentSchema),

  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  Invoice: createModelFromSchema(InvoiceSchema),

  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  Receipt: createModelFromSchema(ReceiptSchema),

  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  TaxUsW2: createModelFromSchema(TaxUsW2Schema),

  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  VaccinationCard: createModelFromSchema(VaccinationCardSchema),

  /**
   * @deprecated will be removed and replaced with auto-generated code in the next stable version
   */
  HealthInsuranceCardUs: createModelFromSchema(HealthInsuranceCardUsSchema)
}; // PrebuiltModels is defined `as const` so this assignment checks to make sure it has the appropriate type, and
// `void __assert_...` simply prevents an unused declaration error

var __assert_prebuiltmodels_has_correct_type = PrebuiltModels;
void __assert_prebuiltmodels_has_correct_type;